import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, FilePdf, Sparkle, SealCheck, ArrowsClockwise, Copy } from "@phosphor-icons/react";
import RamsHazardTable from "../components/hs/RamsHazardTable";
import { RamsMethodSteps, ChipPicker, CoshhTable, OperativesTable } from "../components/hs/RamsEditors";
import PdfBrandingDialog, { downloadBlob } from "../components/hs/PdfBrandingDialog";
import EntityPicker from "../components/hs/EntityPicker";
import NearestAeBox from "../components/hs/NearestAeBox";
import { fmtDateTime } from "../lib/dateUtil";

const STATUS_CLASS = { draft: "bg-slate-100 text-slate-700", issued: "bg-emerald-100 text-emerald-700", superseded: "bg-red-100 text-red-700" };
const EM_FIELDS = [["first_aider", "First aider"], ["first_aid_location", "First-aid kit location"], ["assembly_point", "Fire assembly point"], ["site_contact", "Site emergency contact"]];

function Section({ num, title, hint, children, testId }) {
  return (
    <section className="bg-card border border-border rounded-lg p-5" data-testid={testId}>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="text-xs font-bold text-primary">{num}.</span>
        <h2 className="font-[Chivo] font-semibold text-base">{title}</h2>
        {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

export default function RamsEditorPage() {
  const { ramsId } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [lib, setLib] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ai, setAi] = useState({ open: false, brief: "", busy: false, draft: null });
  const [showBrand, setShowBrand] = useState(false);
  const [showPull, setShowPull] = useState(false);

  const load = useCallback(() => API.get(`/hs/rams/${ramsId}`).then(({ data }) => { setR(data); setDirty(false); }).catch(() => toast.error("RAMS not found")), [ramsId]);
  useEffect(() => { load(); API.get("/hs/library").then(({ data }) => setLib(data)).catch(() => {}); }, [load]);

  const set = (patch) => { setR(x => ({ ...x, ...patch })); setDirty(true); };
  const setJob = (patch) => set({ job: { ...(r.job || {}), ...patch } });
  const setEm = (patch) => set({ emergency: { ...(r.emergency || {}), ...patch } });

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const body = (({ title, summary, scope_of_works, responsibilities, training, welfare, supervisor_name, review_date, approved_by_name, job, hazards, method_steps, ppe, permits, coshh, emergency, operatives }) =>
        ({ title, summary, scope_of_works, responsibilities, training, welfare, supervisor_name, review_date, approved_by_name, job, hazards, method_steps, ppe, permits, coshh, emergency, operatives }))(r);
      const { data } = await API.put(`/hs/rams/${ramsId}`, body);
      setR(data); setDirty(false);
      if (!silent) toast.success("RAMS saved");
      return data;
    } catch (e) { toast.error(formatApiError(e)); throw e; } finally { setSaving(false); }
  };

  const issue = async () => {
    try {
      if (dirty) await save(true);
      const { data } = await API.post(`/hs/rams/${ramsId}/issue`, {});
      setR(data); toast.success(`${data.reference} issued (v${data.version}) — operatives notified`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runAi = async () => {
    setAi(a => ({ ...a, busy: true }));
    try {
      if (dirty) await save(true);
      const { data } = await API.post(`/hs/rams/${ramsId}/ai-draft`, { brief: ai.brief });
      setAi(a => ({ ...a, busy: false, draft: data }));
    } catch (e) { toast.error(formatApiError(e)); setAi(a => ({ ...a, busy: false })); }
  };

  const applyAi = (mode) => {
    const d = ai.draft;
    const merge = (cur, add) => (mode === "replace" ? add : [...(cur || []), ...add]);
    set({
      summary: r.summary && mode === "append" ? r.summary : d.summary || r.summary,
      hazards: merge(r.hazards, d.hazards), method_steps: merge(r.method_steps, d.method_steps), coshh: merge(r.coshh, d.coshh),
      ppe: Array.from(new Set([...(mode === "replace" ? [] : r.ppe || []), ...d.ppe])), permits: Array.from(new Set([...(mode === "replace" ? [] : r.permits || []), ...d.permits])),
      welfare: d.welfare || r.welfare, training: d.training || r.training,
      emergency: { ...(r.emergency || {}), procedures: d.emergency || (r.emergency || {}).procedures },
    });
    setAi({ open: false, brief: "", busy: false, draft: null });
    toast.success(`AI draft ${mode === "replace" ? "applied" : "merged"} — review every line before issuing`);
  };

  const downloadPdf = async (branding) => {
    const resp = branding ? await API.post(`/hs/rams/${ramsId}/pdf`, branding, { responseType: "blob" }) : await API.get(`/hs/rams/${ramsId}/pdf`, { responseType: "blob" });
    downloadBlob(resp.data, `${r.reference}_v${r.version}.pdf`);
    toast.success("RAMS PDF downloaded");
  };

  const pull = async (pick) => {
    try {
      const { data } = await API.post(`/hs/rams/${ramsId}/pull`, { entity_type: pick.entity_type, entity_id: pick.entity_id });
      setR(data); setDirty(false); setShowPull(false); toast.success(`Pulled details from ${pick.reference || pick.title}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const duplicate = async () => {
    const { data } = await API.post(`/hs/rams/${ramsId}/duplicate`, {});
    navigate(`/health-safety/rams/${data.id}`); toast.success(`Copied to ${data.reference}`);
  };

  if (!r) return <div className="flex justify-center py-16"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>;
  const job = r.job || {};
  const permitLabels = lib?.permit_types || [];

  return (
    <div className="space-y-4 pb-24" data-testid="rams-editor-page">
      <div className="flex items-center gap-2 text-sm">
        <Link to="/health-safety" className="text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft size={14} /> Health &amp; Safety</Link>
        <Badge className={`text-[10px] ${STATUS_CLASS[r.status] || ""}`} data-testid="rams-status">{r.status.toUpperCase()} · v{r.version}</Badge>
        {r.status === "issued" && <span className="text-[11px] text-muted-foreground">Editing content will create a new draft version</span>}
      </div>
      {r.auto && r.status === "draft" && (
        <div className="rounded-md border border-violet-200 dark:border-violet-900/60 bg-violet-50/80 dark:bg-violet-950/20 px-4 py-2.5 flex items-start gap-2 text-[12px] text-violet-900 dark:text-violet-200" data-testid="rams-auto-banner">
          <Sparkle size={16} weight="fill" className="shrink-0 mt-0.5" />
          <p>
            {r.auto.ai_status === "drafted"
              ? <>Auto-prepared by AI when the {({ job_request: "job request", job: "job", emergency_job: "emergency job" })[r.auto.kind] || "job"} was created{r.auto.source ? ` from ${({ email: "an email", portal: "the client portal", whatsapp: "WhatsApp", resident: "a resident report", staff: "the office" })[r.auto.source] || r.auto.source}` : ""} — {r.auto.hazards} hazards and {r.auto.steps} method steps. <strong>Check every section before issuing</strong>; the AI cannot see the site.</>
              : <>Created automatically from the job details, but the AI content could not be drafted{r.auto.error ? ` (${r.auto.error})` : ""} — use <strong>AI draft</strong> above or fill it in by hand.</>}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-[260px]">
          <p className="text-xs font-bold text-primary tracking-wide" data-testid="rams-reference">{r.reference}</p>
          <Input value={r.title} onChange={e => set({ title: e.target.value })} className="text-lg font-[Chivo] font-semibold h-10 mt-1" data-testid="rams-title" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowPull(true)} data-testid="rams-pull-btn"><ArrowsClockwise size={14} className="mr-1" /> {job.entity_id ? "Re-pull from job" : "Pull from job"}</Button>
          <Button variant="outline" size="sm" onClick={() => setAi({ open: true, brief: "", busy: false, draft: null })} data-testid="rams-ai-btn"><Sparkle size={14} className="mr-1" /> AI draft</Button>
          <Button variant="outline" size="sm" onClick={duplicate} data-testid="rams-duplicate-btn"><Copy size={14} className="mr-1" /> Duplicate</Button>
          <Button variant="outline" size="sm" onClick={() => setShowBrand(true)} data-testid="rams-pdf-btn"><FilePdf size={14} className="mr-1" /> PDF</Button>
          <Button size="sm" onClick={() => save()} disabled={saving || !dirty} data-testid="rams-save-btn"><FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : dirty ? "Save" : "Saved"}</Button>
          {r.status !== "issued" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={issue} data-testid="rams-issue-btn"><SealCheck size={14} className="mr-1" /> Issue</Button>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section num={1} title="Document control" testId="rams-sec-control">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><Label className="text-[11px]">Prepared by</Label><p className="mt-1 font-medium">{r.prepared_by_name} · {fmtDateTime(r.prepared_at)}</p></div>
            <div><Label className="text-[11px]">Approved by</Label><Input value={r.approved_by_name || ""} onChange={e => set({ approved_by_name: e.target.value })} className="h-8 text-xs mt-1" data-testid="rams-approved-by" /></div>
            <div><Label className="text-[11px]">Supervisor on site</Label><Input value={r.supervisor_name || ""} onChange={e => set({ supervisor_name: e.target.value })} className="h-8 text-xs mt-1" data-testid="rams-supervisor" /></div>
            <div><Label className="text-[11px]">Review date</Label><Input type="date" value={r.review_date || ""} onChange={e => set({ review_date: e.target.value })} className="h-8 text-xs mt-1" data-testid="rams-review-date" /></div>
          </div>
          {r.issued_at && <p className="text-[11px] text-emerald-700 mt-2">Issued {fmtDateTime(r.issued_at)} by {r.issued_by_name} · {(r.operatives_briefed || []).length} operative acknowledgement(s)</p>}
        </Section>
        <Section num={2} title="Project / job details" hint={job.entity_id ? `linked to ${job.reference || job.entity_type}` : "type by hand or pull from a job"} testId="rams-sec-job">
          <div className="grid grid-cols-2 gap-2">
            {[["client_name", "Client"], ["reference", "Job reference"], ["title", "Job title"], ["category", "Trade / category"], ["schedule_text", "Planned dates"], ["site_contact", "Site contact"]].map(([k, l]) => (
              <div key={k}><Label className="text-[11px]">{l}</Label><Input value={job[k] || ""} onChange={e => setJob({ [k]: e.target.value })} className="h-8 text-xs mt-1" data-testid={`rams-job-${k}`} /></div>
            ))}
            <div className="col-span-2"><Label className="text-[11px]">Site address</Label><Input value={job.property_address || ""} onChange={e => setJob({ property_address: e.target.value })} className="h-8 text-xs mt-1" data-testid="rams-job-property_address" /></div>
            {job.safety_notice && <p className="col-span-2 text-[11px] bg-red-50 text-red-700 border border-red-200 rounded p-2" data-testid="rams-job-notice">Job safety notice: {job.safety_notice}</p>}
          </div>
        </Section>
      </div>

      <Section num={3} title="Scope of works" testId="rams-sec-scope">
        <Label className="text-[11px]">Summary for the client</Label>
        <Textarea value={r.summary || ""} onChange={e => set({ summary: e.target.value })} rows={2} className="text-xs mt-1 mb-2" placeholder="2–3 sentences describing the works" data-testid="rams-summary" />
        <Label className="text-[11px]">Detailed scope</Label>
        <Textarea value={r.scope_of_works || ""} onChange={e => set({ scope_of_works: e.target.value })} rows={5} className="text-xs mt-1" placeholder="Pulled from the job's Scope of Works, or type here" data-testid="rams-scope" />
      </Section>

      <Section num={4} title="Personnel & responsibilities" hint="qualifications pulled from the competency matrix" testId="rams-sec-personnel">
        <OperativesTable rows={r.operatives || []} onChange={v => set({ operatives: v })} />
        <div className="grid md:grid-cols-2 gap-2 mt-3">
          <div><Label className="text-[11px]">Required competencies / training</Label><Textarea value={r.training || ""} onChange={e => set({ training: e.target.value })} rows={3} className="text-xs mt-1" data-testid="rams-training" /></div>
          <div><Label className="text-[11px]">Responsibilities</Label><Textarea value={r.responsibilities || ""} onChange={e => set({ responsibilities: e.target.value })} rows={3} className="text-xs mt-1" placeholder="Supervisor, operatives, client/site duties" data-testid="rams-responsibilities" /></div>
        </div>
      </Section>

      <Section num={5} title="Method statement" hint="sequence of works" testId="rams-sec-method">
        <RamsMethodSteps steps={r.method_steps || []} onChange={v => set({ method_steps: v })} />
      </Section>

      <Section num={6} title="Risk assessment" hint="Likelihood × Severity (1–5); residual after controls" testId="rams-sec-hazards">
        <RamsHazardTable hazards={r.hazards || []} onChange={v => set({ hazards: v })} library={lib} />
      </Section>

      <div className="grid md:grid-cols-2 gap-4">
        <Section num={7} title="PPE" testId="rams-sec-ppe">
          <ChipPicker options={lib?.ppe || []} value={r.ppe || []} onChange={v => set({ ppe: v })} testPrefix="rams-ppe" />
        </Section>
        <Section num={8} title="Permits to work" hint="raise the permit itself from the Permits tab" testId="rams-sec-permits">
          <ChipPicker options={permitLabels} value={r.permits || []} onChange={v => set({ permits: v })} labelOf={p => p.label} keyOf={p => p.key} testPrefix="rams-permit" />
        </Section>
      </div>

      <Section num={9} title="COSHH / hazardous substances" testId="rams-sec-coshh">
        <CoshhTable rows={r.coshh || []} onChange={v => set({ coshh: v })} />
      </Section>

      <div className="grid md:grid-cols-2 gap-4">
        <Section num={10} title="Emergency arrangements" testId="rams-sec-emergency">
          <div className="grid grid-cols-1 gap-2">
            {EM_FIELDS.map(([k, l]) => (
              <div key={k}><Label className="text-[11px]">{l}</Label><Input value={(r.emergency || {})[k] || ""} onChange={e => setEm({ [k]: e.target.value })} className="h-8 text-xs mt-1" data-testid={`rams-em-${k}`} /></div>
            ))}
            <NearestAeBox ramsId={ramsId} emergency={r.emergency || {}} hasAddress={!!(r.job || {}).property_address} onChange={(em) => set({ emergency: em })} />
            <div><Label className="text-[11px]">Procedures</Label><Textarea value={(r.emergency || {}).procedures || ""} onChange={e => setEm({ procedures: e.target.value })} rows={4} className="text-xs mt-1" data-testid="rams-em-procedures" /></div>
          </div>
        </Section>
        <Section num={11} title="Welfare, environment & waste" testId="rams-sec-welfare">
          <Textarea value={r.welfare || ""} onChange={e => set({ welfare: e.target.value })} rows={10} className="text-xs" placeholder="Welfare facilities, working hours, noise/dust, waste segregation and disposal" data-testid="rams-welfare" />
        </Section>
      </div>

      <Section num={12} title="Sign-off & operative briefing" hint="operatives acknowledge in their job page" testId="rams-sec-signoff">
        {(r.operatives_briefed || []).length === 0 ? <p className="text-xs text-muted-foreground">No acknowledgements yet{r.status !== "issued" ? " — issue the RAMS to notify the crew." : "."}</p> : (
          <ul className="text-xs space-y-1">{r.operatives_briefed.map((b, i) => <li key={i} className="flex items-center gap-2"><SealCheck size={14} className="text-emerald-600" /> <span className="font-medium">{b.name}</span> <span className="text-muted-foreground">acknowledged v{b.version} · {fmtDateTime(b.at)}</span></li>)}</ul>
        )}
      </Section>

      {/* AI draft dialog */}
      <Dialog open={ai.open} onOpenChange={(o) => !o && setAi({ open: false, brief: "", busy: false, draft: null })}>
        <DialogContent className="max-w-2xl" data-testid="rams-ai-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo] flex items-center gap-2"><Sparkle size={16} className="text-primary" /> AI-assisted RAMS draft</DialogTitle></DialogHeader>
          {!ai.draft ? (
            <>
              <p className="text-xs text-muted-foreground">Uses the job details, scope and crew qualifications already on this RAMS. Add anything site-specific the AI should know.</p>
              <Textarea value={ai.brief} onChange={e => setAi(a => ({ ...a, brief: e.target.value }))} rows={4} placeholder="e.g. Occupied flat, elderly resident. Boiler in kitchen cupboard, flue at first floor needs ladder access from the rear garden." className="text-xs" data-testid="rams-ai-brief" />
              <DialogFooter><Button onClick={runAi} disabled={ai.busy} data-testid="rams-ai-run">{ai.busy ? "Drafting… (20–40s)" : "Draft RAMS"}</Button></DialogFooter>
            </>
          ) : (
            <>
              <div className="text-xs space-y-1 bg-muted/40 rounded-md p-3 max-h-72 overflow-y-auto" data-testid="rams-ai-preview">
                <p className="font-semibold">{ai.draft.hazards.length} hazards · {ai.draft.method_steps.length} method steps · {ai.draft.ppe.length} PPE items · {ai.draft.permits.length} permits · {ai.draft.coshh.length} COSHH</p>
                <p className="text-muted-foreground">{ai.draft.summary}</p>
                <ul className="list-disc pl-4">{ai.draft.hazards.map(h => <li key={h.id}><b>{h.hazard}</b> — {h.rating} → {h.residual}</li>)}</ul>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => applyAi("append")} data-testid="rams-ai-append">Merge with what I have</Button>
                <Button onClick={() => applyAi("replace")} data-testid="rams-ai-replace">Replace sections with draft</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Pull from job */}
      <Dialog open={showPull} onOpenChange={setShowPull}>
        <DialogContent className="max-w-lg" data-testid="rams-pull-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Pull details from a job</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Client, site, scope, dates, site contact and the assigned crew (with their qualifications) are copied onto this RAMS.</p>
          <EntityPicker onPick={pull} />
        </DialogContent>
      </Dialog>

      <PdfBrandingDialog open={showBrand} onOpenChange={setShowBrand} clientId={r.client_id || job.client_id} onDownload={downloadPdf} title="RAMS PDF branding"
        description="Brand the RAMS for the client exactly like your quote PDFs — logo, company name, colour and footer. Leave blank for your own branding." />
    </div>
  );
}
